import { useState, useEffect, useMemo } from "react";
import { ChevronRight, Building2 } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import { stages, normalizeStage, badgeTone, compactMoney, testid } from "../../lib/format";
import { isSuperAdmin } from "../../lib/permissions";
import { api } from "../../lib/api";

const PIE_COLORS = ["#2563eb", "#f59e0b", "#16a34a", "#dc2626"];

function Metric({ label, value, caption, tone = "blue", onClick }) {
  return (
    <div
      className={`metric ${tone} ${onClick ? "metric-clickable clickable" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(e);
        }
      } : undefined}
      data-testid={`metric-${testid(label)}`}
      title={onClick ? `View ${label}` : undefined}
      style={{
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
      }}
    >
      <div className="metric-top">
        <span>{label}</span>
        {onClick && (
          <span className="metric-nav-arrow" aria-hidden="true" style={{ opacity: 0.45, fontSize: 13, fontWeight: 700 }}>
            →
          </span>
        )}
      </div>
      <strong>{value}</strong>
      <small>{caption}</small>
    </div>
  );
}

function OrderRow({ o, onOpen }) {
  const stage = normalizeStage(o.current_stage);
  return (
    <tr onClick={() => onOpen(o.order_id)} data-testid={`order-row-${o.order_id}`}>
      <td><b className="mono">{o.order_id}</b><small>{o.order_date}</small></td>
      <td><b>{o.client_name}</b><small>{o.po_number || "—"}</small></td>
      <td>{o.product}<small>{(o.quantity || 0).toLocaleString()} units</small></td>
      <td>{o.required_delivery_date || "—"}</td>
      <td><b>{compactMoney(o.total_value)}</b></td>
      <td><span className={`badge ${badgeTone(stage)}`}>{stage}</span></td>
    </tr>
  );
}

export default function Dashboard({
  data: propData,
  orders: propOrders = [],
  onOpen,
  onNavigate,
  user,
  selectedCompany = "All Companies",
  onCompanyChange,
  companies = [],
}) {
  const [localCompanies, setLocalCompanies] = useState(companies);
  const [currentCompany, setCurrentCompany] = useState(selectedCompany || "All Companies");
  const [internalData, setInternalData] = useState(propData || null);
  const [internalOrders, setInternalOrders] = useState(propOrders || []);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (propData) {
      setInternalData(propData);
    }
  }, [propData]);

  useEffect(() => {
    if (propOrders) {
      setInternalOrders(propOrders);
    }
  }, [propOrders]);

  useEffect(() => {
    if (selectedCompany !== undefined && selectedCompany !== null) {
      setCurrentCompany(selectedCompany);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (companies && companies.length > 0) {
      setLocalCompanies(companies);
    } else if (isSuperAdmin(user)) {
      api("get", "/companies?active_only=true")
        .then((res) => {
          if (Array.isArray(res.data)) {
            setLocalCompanies(res.data);
          }
        })
        .catch(() => {});
    }
  }, [companies, user]);

  const activeCompanies = useMemo(() => {
    const list = localCompanies && localCompanies.length > 0 ? localCompanies : companies;
    const seen = new Set();
    const result = [];
    for (const c of list || []) {
      if (c && c.is_active !== false) {
        const name = (c.name || c.company_name || "").trim();
        if (name && !seen.has(name.toLowerCase())) {
          seen.add(name.toLowerCase());
          result.push({ ...c, name });
        }
      }
    }
    return result;
  }, [localCompanies, companies]);

  const handleSelectCompany = async (compName) => {
    setCurrentCompany(compName);
    if (onCompanyChange) {
      onCompanyChange(compName);
    }
    try {
      setIsFetching(true);
      const isAll = !compName || compName === "All Companies";
      const dashUrl = isAll ? "/dashboard" : `/dashboard?company=${encodeURIComponent(compName)}`;
      const ordersUrl = isAll ? "/orders" : `/orders?company=${encodeURIComponent(compName)}`;
      const [d, o] = await Promise.all([
        api("get", dashUrl),
        api("get", ordersUrl),
      ]);
      setInternalData(d.data);
      setInternalOrders(o.data);
    } catch (err) {
      console.error("Failed to load company dashboard data", err);
    } finally {
      setIsFetching(false);
    }
  };

  const activeData = internalData || propData || {};
  const activeOrdersList = (internalOrders && internalOrders.length > 0) ? internalOrders : propOrders;

  const months = activeData?.monthly || [];
  const deliveryData = Object.entries(activeData?.delivery_counts || {}).map(([name, value]) => ({ name, value }));
  const stageData = stages.map((s) => ({
    stage: s,
    count: activeData?.stage_counts?.[s] || 0,
  }));

  const displayOrders = (activeData?.recent_orders && activeData.recent_orders.length > 0)
    ? activeData.recent_orders
    : activeOrdersList;
  const isSuper = isSuperAdmin(user);

  return (
    <div className="content" style={{ opacity: isFetching ? 0.7 : 1, transition: "opacity 0.15s ease" }}>
      <div
        className="page-heading"
        style={{
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div>
            <div className="small-label">
              {new Date()
                .toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
                .toUpperCase()}
            </div>
            <h1 style={{ margin: "2px 0 3px", fontSize: 24, fontWeight: 800 }}>Dashboard</h1>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Every order, every stage — visible at a glance.</p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {isSuper && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#ffffff",
                border: "1px solid var(--line, #e2e8f0)",
                borderRadius: 8,
                padding: "2px 10px 2px 12px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                minHeight: 38,
              }}
              data-testid="company-dropdown-container"
            >
              <Building2 size={16} style={{ color: "#64748b", flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", whiteSpace: "nowrap" }}>
                Company:
              </span>
              <select
                value={currentCompany || "All Companies"}
                onChange={(e) => handleSelectCompany(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1e293b",
                  cursor: "pointer",
                  padding: "6px 8px 6px 4px",
                  height: 36,
                  minWidth: 160,
                }}
                data-testid="dashboard-company-dropdown"
                aria-label="Filter dashboard by company"
              >
                <option value="All Companies">All Companies</option>
                {activeCompanies.map((c) => {
                  const cName = c.name || c.company_name;
                  return (
                    <option key={c.company_id || cName} value={cName}>
                      {cName}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="metrics-grid">
        <Metric
          label="Total orders"
          value={activeData?.total_orders || 0}
          caption="Across all workflows"
          tone="blue"
          onClick={() => onNavigate({ page: "Orders", company: currentCompany })}
        />
        <Metric
          label="Awaiting approval"
          value={activeData?.awaiting_approval || 0}
          caption="Client action pending"
          tone="amber"
          onClick={() => onNavigate({ page: "Approvals", activeTab: "pending", company: currentCompany })}
        />
        <Metric
          label="In production"
          value={activeData?.in_production || 0}
          caption="On the shop floor"
          tone="blue"
          onClick={() => onNavigate({ page: "Production", tab: "orders", company: currentCompany })}
        />
        <Metric
          label="Ready for dispatch"
          value={activeData?.ready_dispatch || 0}
          caption="Production complete"
          tone="green"
          onClick={() => onNavigate({ page: "Dispatch", statusFilter: "Needs Staff", company: currentCompany })}
        />
      </div>

      <div className="metrics-grid">
        <Metric
          label="In transit"
          value={activeData?.in_transit ?? activeData?.awaiting_delivery ?? 0}
          caption="Out for delivery"
          tone="blue"
          onClick={() => onNavigate({ page: "Delivery", statusFilter: "Out for Delivery", company: currentCompany })}
        />
        <Metric
          label="Pending delivery"
          value={activeData?.pending_delivery || 0}
          caption="Awaiting client receipt"
          tone="amber"
          onClick={() => onNavigate({ page: "Delivery", statusFilter: "Pending", company: currentCompany })}
        />
        <Metric
          label="Delivered"
          value={activeData?.delivered || 0}
          caption="Successfully fulfilled"
          tone="green"
          onClick={() => onNavigate({ page: "Delivery", statusFilter: "Delivered", company: currentCompany })}
        />
        <Metric
          label="Overdue orders"
          value={activeData?.overdue_orders || 0}
          caption="Past required date"
          tone="red"
          onClick={() => onNavigate({ page: "Orders", filter: "overdue", company: currentCompany })}
        />
      </div>

      <div className="dashboard-grid">
        <section className="panel chart-panel">
          <div className="panel-title">
            <div>
              <h3>Order volume trend</h3>
              <p className="muted">Monthly order intake and fulfillment · Click any point to view orders</p>
            </div>
          </div>
          <div style={{ width: "100%", height: 220 }} data-testid="order-volume-chart">
            <ResponsiveContainer>
              <LineChart
                data={months}
                onClick={(e) => {
                  if (e && e.activePayload && e.activePayload.length > 0) {
                    const monthVal = e.activePayload[0].payload?.month;
                    if (monthVal) {
                      onNavigate({ page: "Orders", month: monthVal, company: currentCompany });
                    }
                  } else if (e && e.activeLabel) {
                    onNavigate({ page: "Orders", month: e.activeLabel, company: currentCompany });
                  }
                }}
              >
                <CartesianGrid stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" stroke="#475569" fontSize={11} />
                <YAxis allowDecimals={false} stroke="#475569" fontSize={11} />
                <Tooltip formatter={(v) => [`${v} order${v === 1 ? "" : "s"}`, "Orders"]} />
                <Line
                  type="monotone"
                  dataKey="orders"
                  stroke="#2563eb"
                  strokeWidth={2.5}
                  dot={{ r: 4, cursor: "pointer", fill: "#2563eb" }}
                  activeDot={{ r: 6, cursor: "pointer", fill: "#1d4ed8", stroke: "#ffffff", strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="panel">
          <div className="panel-title">
            <div>
              <h3>Delivery status</h3>
              <p className="muted">Where every order stands · Click segments to view deliveries</p>
            </div>
          </div>
          <div style={{ width: "100%", height: 220 }} data-testid="delivery-status-chart">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={deliveryData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={80}
                  cursor="pointer"
                  onClick={(entry) => {
                    if (!entry) return;
                    const rawName = entry.name || entry.payload?.name;
                    const statusMap = {
                      "In Transit": "Out for Delivery",
                      "Pending": "Pending",
                      "Delivered": "Delivered",
                    };
                    const targetStatus = statusMap[rawName] || rawName;
                    onNavigate({ page: "Delivery", statusFilter: targetStatus, company: currentCompany });
                  }}
                >
                  {deliveryData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} cursor="pointer" />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [`${v} order${v === 1 ? "" : "s"}`, "Orders"]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="legend">
            {deliveryData.map((d, i) => (
              <span
                key={d.name}
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => {
                  const statusMap = {
                    "In Transit": "Out for Delivery",
                    "Pending": "Pending",
                    "Delivered": "Delivered",
                  };
                  const targetStatus = statusMap[d.name] || d.name;
                  onNavigate({ page: "Delivery", statusFilter: targetStatus, company: currentCompany });
                }}
                title={`View ${d.name} deliveries`}
                data-testid={`delivery-legend-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <i style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                {d.name} · {d.value}
              </span>
            ))}
          </div>
        </section>
      </div>

      <section className="panel">
        <div className="panel-title">
          <div>
            <h3>Orders by stage</h3>
            <p className="muted">Workflow pulse across the active pipeline · Click any stage bar to filter orders</p>
          </div>
        </div>
        <div style={{ width: "100%", height: 240 }} data-testid="orders-by-stage-chart">
          <ResponsiveContainer>
            <BarChart
              data={stageData}
              onClick={(e) => {
                if (e && e.activePayload && e.activePayload.length > 0) {
                  const stg = e.activePayload[0].payload?.stage;
                  if (stg) {
                    onNavigate({ page: "Orders", stage: stg, company: currentCompany });
                  }
                }
              }}
            >
              <CartesianGrid stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="stage" stroke="#475569" fontSize={11} />
              <YAxis allowDecimals={false} stroke="#475569" fontSize={11} />
              <Tooltip formatter={(v) => [`${v} order${v === 1 ? "" : "s"}`, "Orders"]} />
              <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} cursor="pointer" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="panel recent-panel">
        <div className="panel-title">
          <div>
            <h3>Recent orders</h3>
            <p className="muted">
              {currentCompany && currentCompany !== "All Companies"
                ? `Latest client work for ${currentCompany}`
                : "Latest client work in motion"}
            </p>
          </div>
          <button className="text-btn" data-testid="view-all-orders-button" onClick={() => onNavigate("Orders")}>
            View all <ChevronRight size={15} />
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Client</th>
                <th>Product</th>
                <th>Delivery</th>
                <th>Value</th>
                <th>Stage</th>
              </tr>
            </thead>
            <tbody>
              {displayOrders.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "32px 16px", color: "#64748b" }}>
                    No recent orders found{currentCompany && currentCompany !== "All Companies" ? ` for ${currentCompany}` : ""}.
                  </td>
                </tr>
              ) : (
                displayOrders.slice(0, 6).map((o) => (
                  <OrderRow key={o.order_id} o={o} onOpen={onOpen} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

